import React, { FunctionComponent, useContext, useEffect, useState } from 'react';
import AuthModalPage from '@/components/pageComponents/AuthModalPage';
import { FrontPluginContext } from '@/contexts/FrontPluginContext';
import useAuthIsHydrated from '@/hooks/useAuthIsHydrated';
import useCurrentUser from '@jetslash/market-frontend-shared-core/src/hooks/useCurrentUser';
import { SignUpWizardContextProvider } from '@/contexts/SignUpWizardContext';
import useUserReturnTo from '@/utility/session/cookies/useUserReturnTo';
import { RedirectMsgParam } from '@/hooks/useRedirectMsg';
import FullScreenLoadingOverlay from '@/components/loading/FullScreenLoadingOverlay';

interface IProps {
  type?: 'redirect' | 'modal' | 'lax';
  redirectMsg?: RedirectMsgParam;
  showLoading?: boolean;
  children: React.ReactNode;
}

/**
 * ProtectedPage
 * @param type
 * 'redirect' will redirect the user from the page as soon as they land on it
 * 'modal' will display a login modal over top of the children
 * 'lax' will just block content until currentUser and auth state is known, regardless of whether or not the user is actually logged in
 * This is used for pages that are dependent on user authentication state, but should still be displayed to everyone
 * @param children
 * @constructor
 *
 * For future consideration:
 *  - ProtectedPage probably isn't the best name. It could possibly be split into two components: one that blocks the page until user info is hydrated, and another that defines the Protection logic
 *  - We might have the ordering of this all wrong. It possible that we should wrap EVERYTHING in a 'wait for hydration' block in _app.tsx
 *  - The 'wait for hydration' logic is meant to prevent us from firing off requests until we have hydrated the access token on page load. There might be a more graceful way to handle this
 */
// @ts-ignore
const ProtectedPage: FunctionComponent<IProps> = ({ children, type = 'redirect', redirectMsg, showLoading }) => {
  const authIsHydrated = useAuthIsHydrated();
  const { currentUserIsAuthenticated, currentUserIsHydrated, currentUser } = useCurrentUser();
  const { onFrontPluginPage } = useContext(FrontPluginContext);
  const { recordUserReturnTo } = useUserReturnTo();
  const [userWasAuthenticated, setUserWasAuthenticated] = useState(false);
  const currentPageLocation = `${globalThis?.location?.pathname}${globalThis?.location?.search}`;

  useEffect(() => {
    if (currentUserIsAuthenticated && !userWasAuthenticated) {
      setUserWasAuthenticated(true);
    }
  }, [currentUserIsAuthenticated]);

  useEffect(() => {
    // If the user is not authenticated, record the current page as the return to page
    if ((type === 'redirect' || type === 'modal') && !currentUserIsAuthenticated) {
      recordUserReturnTo(currentPageLocation);
    }
  }, [currentPageLocation, currentUserIsAuthenticated]);

  if (!(authIsHydrated && currentUserIsHydrated)) {
    return showLoading ? <FullScreenLoadingOverlay loading noHeader noFooter transparent /> : null;
  }

  if (currentUserIsAuthenticated) {
    return children;
  }

  if (type === 'lax') {
    return children;
  }
  if (type === 'redirect' || userWasAuthenticated) {
    // By checking if user WAS authenticated, we know to not pop up the modal on a logout and instead just redirect\
    let redirectUrl = '/users/log-in';
    if (currentUser && !currentUserIsAuthenticated) {
      redirectUrl = '/users/sign-up';
    }
    if (redirectMsg) {
      redirectUrl += `?from=${redirectMsg}`;
    }
    window.location.href = redirectUrl;
    return null;
  }
  if (type === 'modal') {
    const loginModal = (
      <SignUpWizardContextProvider>
        <AuthModalPage logInOnly={onFrontPluginPage} />
      </SignUpWizardContextProvider>
    );

    return (
      <>
        {children}
        {loginModal}
      </>
    );
  }
  throw new Error('ProtectedPage type is invalid');
};

export default ProtectedPage;
